import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Space,
  Select,
  Alert,
  DatePicker,
  Tooltip,
  Tag,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import { apiService } from '../services/api';
import dayjs from 'dayjs';
import { formatDate } from '../utils/date';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SwapOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { Owner } from '../types';

const { Option } = Select;

export const Owners: React.FC = () => {
  // Sprint 5e — narrow selectors instead of whole-store destructuring.
  // The Owners table rerenders only when its slice changes, not when an
  // unrelated mutation hits the store. Action references are stable
  // across renders so no `shallow` is needed.
  const owners              = useCondoStore((s: any) => s.owners) as Owner[];
  const fetchOwners         = useCondoStore((s: any) => s.fetchOwners);
  const createOwner         = useCondoStore((s: any) => s.createOwner);
  const updateOwnerApi      = useCondoStore((s: any) => s.updateOwnerApi);
  const deleteOwnerApi      = useCondoStore((s: any) => s.deleteOwnerApi);
  const recordUnitTransfer  = useCondoStore((s: any) => s.recordUnitTransfer);
  const { currentCondo } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);
  const [form] = Form.useForm();

  // S8.2 — Transfer state
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferringOwner, setTransferringOwner] = useState<Owner | null>(null);
  const [transferForm] = Form.useForm();
  const [transferring, setTransferring] = useState(false);

  // Sprint 34 — finding G1 (round-4 e2e). Without the condoId arg this
  // fetched ALL owners across every tenant — super-admin on Oak Ridge
  // saw Maple's residents in the table. Same bug class as the
  // Sprint 30 F15 dashboard KPI fix, reapplied at the feature-page
  // layer. Re-runs when the user switches condos via the header
  // dropdown so the table refreshes.
  useEffect(() => {
    if (currentCondo?.id) {
      fetchOwners(currentCondo.id);
    }
  }, [fetchOwners, currentCondo?.id]);

  const handleSubmit = async (values: any) => {
    // Sprint 29 — finding N1/F13. DatePicker emits a Dayjs instance;
    // backend (and the rest of the store) expects a Date object built
    // from a 'YYYY-MM-DD' string. Normalize here.
    const moveInDate = values.moveInDate?.format
      ? new Date(values.moveInDate.format('YYYY-MM-DD'))
      : new Date(values.moveInDate);
    const ownerData = {
      ...values,
      moveInDate,
      condoId: currentCondo?.id,
    };

    try {
      if (editingOwner) {
        await updateOwnerApi(editingOwner.id, ownerData);
        staticApi.message.success('Owner updated successfully');
      } else {
        await createOwner(ownerData);
        staticApi.message.success('Owner added successfully');
      }
      setIsModalOpen(false);
      setEditingOwner(null);
      form.resetFields();
    } catch (error) {
      // Sprint 117 — surface the backend's specific RFC-7807 message (e.g.
      // "Phone number should be valid", a 409 duplicate, a 400 validation
      // failure) instead of a generic toast. apiJson attaches `.message`
      // (best human-readable string) and `.problem` (structured detail +
      // fieldErrors). Keep the modal OPEN on a 4xx so the user can correct
      // and retry — matches the existing 409 behaviour.
      const err = error as { message?: string; problem?: { status?: number; detail?: string; fieldErrors?: Record<string, string> } };
      // Optional inline mapping: a field-scoped validation error lands under
      // its input when the field name matches a form field; otherwise we fall
      // through to the toast only.
      if (err.problem?.fieldErrors) {
        const known = new Set(['name', 'unitNumber', 'email', 'phone', 'moveInDate']);
        const inlineFields = Object.entries(err.problem.fieldErrors)
          .filter(([name]) => known.has(name))
          .map(([name, msg]) => ({ name, errors: [msg] }));
        if (inlineFields.length) form.setFields(inlineFields);
      }
      // `err.message` already encodes the priority (detail → first field-error
      // → title → status), so it's the most specific string. For a bean-
      // validation 400 it is the field message, not the generic
      // "Request validation failed" detail.
      staticApi.message.error(err.message || 'Operation failed. Please try again.');
    }
  };

  const handleEdit = (owner: Owner) => {
    setEditingOwner(owner);
    // Sprint 29 — feed DatePicker a Dayjs instance, not a 'YYYY-MM-DD'
    // string (which it interprets as undefined and clears the field).
    form.setFieldsValue({
      ...owner,
      moveInDate: owner.moveInDate ? dayjs(owner.moveInDate) : null,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    staticApi.modal.confirm({
      title: 'Are you sure you want to delete this owner?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteOwnerApi(id);
          staticApi.message.success('Owner deleted successfully');
        } catch {
          staticApi.message.error('Failed to delete owner.');
        }
      },
    });
  };

  // Sprint 124 (ADR-117) — resend the activation invite for an owner whose
  // login is still unclaimed. The endpoint answers 200 regardless (no
  // enumeration); dev logs the link, prod emails it.
  const handleResendInvite = async (owner: Owner) => {
    if (!owner.userId) {
      staticApi.message.warning('This owner has no linked login account yet.');
      return;
    }
    try {
      await apiService.resendOwnerInvite(owner.userId);
      staticApi.message.success(`Activation invite re-sent to ${owner.email}.`);
    } catch {
      staticApi.message.error('Failed to resend the invite.');
    }
  };

  // S8.2 — Open transfer modal
  const handleOpenTransfer = (owner: Owner) => {
    setTransferringOwner(owner);
    transferForm.resetFields();
    setTransferModalOpen(true);
  };

  // S8.2 — Execute unit transfer
  const handleTransferSubmit = async () => {
    try {
      const values = await transferForm.validateFields();
      setTransferring(true);

      const newOwner = owners.find((o: Owner) => o.id === values.newOwnerId);

      // Log the transfer history
      await recordUnitTransfer({
        condoId: currentCondo?.id || '',
        unitNumber: transferringOwner?.unitNumber || '',
        previousOwnerId: transferringOwner?.id,
        previousOwnerName: transferringOwner?.name,
        newOwnerId: newOwner?.id,
        newOwnerName: newOwner?.name,
        // Sprint 29 — DatePicker emits Dayjs; serialize to YYYY-MM-DD
        // for the backend (which expects an ISO date string here).
        effectiveDate: values.effectiveDate?.format
          ? values.effectiveDate.format('YYYY-MM-DD')
        // Sprint 145 (R14-9) — the highest-stakes instance of the UTC-stamp
        // bug: an ownership transfer recorded after 20:00 EDT took effect a day
        // later than the manager selected. `dayjs()` is local.
          : (values.effectiveDate || dayjs().format('YYYY-MM-DD')),
        notes: values.notes,
      });

      // Update the new owner's unit number via updateOwnerApi
      if (newOwner && transferringOwner) {
        await updateOwnerApi(newOwner.id, { ...newOwner, unitNumber: transferringOwner.unitNumber });
        await updateOwnerApi(transferringOwner.id, { ...transferringOwner, unitNumber: '' });
      }

      staticApi.message.success(`Unit ${transferringOwner?.unitNumber} transferred to ${newOwner?.name}`);
      setTransferModalOpen(false);
      // Sprint 34 — same scoping fix as the useEffect above. Post-
      // transfer refresh must stay scoped to the active condo.
      fetchOwners(currentCondo?.id);
    } catch {
      staticApi.message.error('Transfer failed. Please try again.');
    } finally {
      setTransferring(false);
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Unit', dataIndex: 'unitNumber', key: 'unitNumber' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    {
      title: 'Move-in Date',
      dataIndex: 'moveInDate',
      key: 'moveInDate',
      // Sprint 29 — finding F12. Was Date.toLocaleDateString() which
      // renders in browser locale (3/20/2022 in en-US, 20/3/2022 in
      // en-GB, etc.) — ambiguous on a Canadian-data app.
      // Sprint 145 (R14-10) — Sprint 29 reached the right conclusion (ambiguity
      // is the harm) and then implemented it privately, which is how the tree
      // ended up with several correct-but-divergent date treatments. It now
      // delegates to the shared `formatDate`, which produces the identical
      // 'MMM D, YYYY' shape. The nowrap wrapper stays (S3 sibling).
      render: (date: Date | string) => {
        if (!date) return '';
        return <span style={{ whiteSpace: 'nowrap' }}>{formatDate(date)}</span>;
      },
    },
    {
      // Sprint 124 (ADR-117) — who can actually log in. Invited = a login
      // exists but the owner has never set a password; Active = claimed;
      // No login = no linked account (the cross-condo un-provisioned edge).
      title: 'Account',
      key: 'accountState',
      render: (_: any, record: Owner) => {
        const state = record.accountState;
        if (state === 'ACTIVE') return <Tag color="green">Active</Tag>;
        if (state === 'NO_LOGIN') return <Tag color="default">No login</Tag>;
        // Default / INVITED
        return <Tag color="gold">Invited</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Owner) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          {record.accountState !== 'ACTIVE' && record.userId && (
            <Tooltip title="Resend the account activation link">
              <Button
                size="small"
                icon={<MailOutlined />}
                onClick={() => handleResendInvite(record)}
              >
                Resend invite
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Transfer unit to another owner">
            <Button
              size="small"
              icon={<SwapOutlined />}
              onClick={() => handleOpenTransfer(record)}
              style={{ color: '#722ed1', borderColor: '#722ed1' }}
            >
              Transfer
            </Button>
          </Tooltip>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Owners</h1>
          <p className="text-gray-600">Manage condo unit owners</p>
        </div>
        <Button type="dashed" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
          Add Owner
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          // Sprint 34 — finding G1 belt-and-suspenders filter. Even with
          // the useEffect re-fetch above, the store can briefly hold
          // owners from the previous condo while the new fetch is in
          // flight; defensive client-side filter keeps the table
          // honest about who lives in *this* condo. An empty currentCondo
          // (admin without binding) renders no owners.
          dataSource={currentCondo
            ? owners.filter((o: any) => o.condoId === currentCondo.id)
            : []}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingOwner ? 'Edit Owner' : 'Add New Owner'}
        open={isModalOpen}
        onCancel={() => { setIsModalOpen(false); setEditingOwner(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={editingOwner ? 'Update Owner' : 'Add Owner'}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                <Input placeholder="Enter owner name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="unitNumber" label="Unit Number" rules={[{ required: true }]}>
                <Input placeholder="Enter unit number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]}>
            <Input placeholder="Enter email address" />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input placeholder="Enter phone number" />
          </Form.Item>
          {/* Sprint 29 — finding F13. Native <input type="date"> renders
              in the browser locale (US M/D/YYYY vs Canadian D/M/YYYY).
              Sprint 27 N1 fixed Violations; this is the Resident
              Management sibling. */}
          <Form.Item name="moveInDate" label="Move-in Date" rules={[{ required: true }]}>
            <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* S8.2 — Unit Transfer Modal */}
      <Modal
        title={
          <Space>
            <SwapOutlined style={{ color: '#722ed1' }} />
            Transfer Unit {transferringOwner?.unitNumber}
          </Space>
        }
        open={transferModalOpen}
        onCancel={() => setTransferModalOpen(false)}
        onOk={handleTransferSubmit}
        okText="Confirm Transfer"
        confirmLoading={transferring}
        width={520}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          message={`This will transfer unit ${transferringOwner?.unitNumber} from ${transferringOwner?.name} to the selected owner and log the handover.`}
          style={{ marginBottom: 16 }}
        />
        <Form form={transferForm} layout="vertical">
          <Form.Item name="newOwnerId" label="Transfer To (New Owner)" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Select new owner"
              optionFilterProp="children"
            >
              {owners
                .filter((o: Owner) => o.id !== transferringOwner?.id)
                .map((o: Owner) => (
                  <Option key={o.id} value={o.id}>
                    {o.name} — Unit {o.unitNumber || 'Unassigned'}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item name="effectiveDate" label="Effective Date">
            <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Reason for transfer, handover conditions, etc." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
